// src/helpers/DataLoader.js
import { pinia } from '@/main' // Import the Pinia instance
import { useActionsStore } from '@/stores/actions-store'
import { useInsightsStore } from '@/stores/insights-store'
import { useResponsesStore } from '@/stores/responses-store'
import { useResultsStore } from '@/stores/results-store'
import { useSurveysStore } from '@/stores/surveys-store'
import { useStatesStore } from '@/stores/states-store'
import { requestTypes } from '@/constants/request-types'
import { oldSurveysWithinTimePeriod } from './helpers/trend-store-helper'
// import questionStats from './fixtures/question-stats-chtest-npburzc9.json'
// import surveyDetails from './fixtures/survey-details-chtest-npburzc9.json'

const loadingPromises = {}

export const storeDataKeyToRequestTypeMap = {
  actions: requestTypes.ACTIONS,
  categories: requestTypes.GROUP_RESULTS_FOR_SURVEY,
  tagsResultsPerPerson: requestTypes.TAGS_RESULTS_PER_PERSON,
  categoriesResultsPerPerson: requestTypes.CATEGORIES_RESULTS_PER_PERSON,
  insightsTeam: requestTypes.INSIGHTS_TEAM,
  insightsLeadership: requestTypes.INSIGHTS_LEADERSHIP,
  questions: requestTypes.GROUP_RESULTS_FOR_SURVEY,
  questionResultsForSurvey: requestTypes.QUESTION_RESULTS_FOR_SURVEY,
  responses: requestTypes.RESPONSES,
  surveyDetails: requestTypes.SURVEY_DETAILS,
  surveyApiSurveyDetails: requestTypes.FILTERED_SURVEY_DETAILS,
  surveys: requestTypes.SURVEYS,
  tags: requestTypes.GROUP_RESULTS_FOR_SURVEY,
  trendCategories: requestTypes.GROUP_RESULTS_FOR_SURVEY,
  trendQuestions: requestTypes.GROUP_RESULTS_FOR_SURVEY,
  trendTags: requestTypes.GROUP_RESULTS_FOR_SURVEY
}

export const requestNameToStoreMap = {
  [requestTypes.ACTIONS]: 'actionsStore',
  [requestTypes.INSIGHTS_TEAM]: 'insightsStore',
  [requestTypes.INSIGHTS_LEADERSHIP]: 'insightsStore',
  [requestTypes.RESPONSES]: 'responsesStore',
  [requestTypes.TAGS_RESULTS_PER_PERSON]: 'resultsStore',
  [requestTypes.CATEGORIES_RESULTS_PER_PERSON]: 'resultsStore',
  [requestTypes.QUESTION_RESULTS_FOR_SURVEY]: 'resultsStore',
  [requestTypes.TREND_TAGS]: 'resultsStore',
  [requestTypes.TREND_CATEGORIES]: 'resultsStore',
  [requestTypes.TREND_QUESTIONS]: 'resultsStore',
  [requestTypes.TAGS]: 'resultsStore',
  [requestTypes.CATEGORIES]: 'resultsStore',
  [requestTypes.QUESTIONS]: 'resultsStore',
  [requestTypes.SURVEYS]: 'surveysStore',
  [requestTypes.SURVEY_DETAILS]: 'surveysStore',
  [requestTypes.FILTERED_SURVEY_DETAILS]: 'surveysStore',
  organizationName: 'statesStore',
  selectedGroupName: 'statesStore'
}

export async function makeDataRequest({
  dataName,
  widgetData,
  requestData,
  surveyName,
  groupName
}) {
  // If data is being loaded, return the existing promise
  if (loadingPromises[dataName]) {
    return loadingPromises[dataName]
  }
  // Load the data and store the promise
  loadingPromises[dataName] = loadData({
    dataName,
    widgetData,
    requestData,
    surveyName,
    groupName
  })

  try {
    const data = await loadingPromises[dataName]
    delete loadingPromises[dataName]
    return data
  } catch (error) {
    console.error(`Error loading data for ${dataName}:`, error)
    delete loadingPromises[dataName]
    throw error
  }
}

// const mockData = {
//   groupResultsForSurvey: questionStats,
//   surveyDetails,
//   surveys: ['NPBURZc9']
// }

export async function loadData({
  dataName,
  widgetData,
  requestData,
  surveyName,
  groupName
}) {
  // Get the store instance
  const actionsStore = useActionsStore(pinia)
  const insightsStore = useInsightsStore(pinia)
  const resultsStore = useResultsStore(pinia)
  const responsesStore = useResponsesStore(pinia)
  const statesStore = useStatesStore(pinia)
  const surveysStore = useSurveysStore(pinia)

  const surveyNameToLoad = surveyName || statesStore.getSurveyName
  // const groupName

  const aggregate = statesStore.surveyIsAggregate
  const surveyIsAggregateV2 = statesStore.surveyIsAggregateV2
  const oldSurveys = oldSurveysWithinTimePeriod({
    surveyDetails: surveysStore.getSurveyDetails,
    selectedTimePeriod: statesStore.getSelectedTimePeriod
  })

  const selectedAttributeSegment = statesStore.getSelectedAttributeSegment

  switch (dataName) {
    case requestTypes.ACTIONS:
      try {
        const requestedSummary = requestData?.params?.summaryActions || null
        const existingActions = actionsStore.actions || {}
        const hasActionsArray = Array.isArray(existingActions.actions)
        const existingSummaryKeys = Object.keys(
          existingActions.summaryActions || {}
        )
        const requestedSummaryKeys = Object.keys(requestedSummary || {})
        const missingRequestedSummaries = requestedSummaryKeys.some(
          (k) => !existingSummaryKeys.includes(k)
        )
        const shouldLoadActions = !hasActionsArray || missingRequestedSummaries
        if (shouldLoadActions) {
          await actionsStore.loadGroupActionsAction({
            groupName,
            surveyName: surveyNameToLoad,
            aggregate,
            summaryActions: requestedSummary,
            doneOnly: requestData.params?.doneOnly
          })
        }
      } catch (e) {
        // Fall back to loading on any unexpected condition
        await actionsStore.loadGroupActionsAction({
          groupName,
          surveyName: surveyNameToLoad,
          aggregate,
          summaryActions: requestData.params?.summaryActions,
          doneOnly: requestData.params?.doneOnly
        })
      }
      return actionsStore.actions

    case requestTypes.INSIGHTS_TEAM:
    case requestTypes.INSIGHTS_LEADERSHIP:
      if (widgetData.area && !insightsStore[dataName]?.insights) {
        const shouldLoadGeneralInsights = aggregate && !surveyIsAggregateV2
        await insightsStore.loadInsightsAction({
          surveyName: shouldLoadGeneralInsights ? 'ALL' : surveyNameToLoad,
          groupName,
          area: widgetData.area
        })
      }
      return insightsStore.insights

    case requestTypes.GROUP_RESULTS_FOR_SURVEY:
      const surveysToLoad = [surveyNameToLoad].concat(oldSurveys)
      if (
        surveysToLoad.length !== resultsStore.currentlyLoadedSurveys.length ||
        !surveysToLoad.every((s) =>
          resultsStore.currentlyLoadedSurveys.includes(s)
        )
      ) {
        await resultsStore.loadGroupResultsForSurveyAction({
          surveyName: surveyNameToLoad,
          groupName,
          aggregate,
          oldSurveys,
          selectedAttributeSegment
        })
      }
      return surveysStore.groupResultsForSurvey

    case requestTypes.QUESTION_RESULTS_FOR_SURVEY:
      const categoryName = Object.keys(requestData.params)[0] // category name and question name are required
      const questionName = Object.keys(requestData.params[categoryName])[0]
      await resultsStore.loadQuestionResponsesForSurveyAction({
        surveyName: surveyNameToLoad,
        groupName,
        categoryName,
        questionName,
        aggregate,
        selectedAttributeSegment
      })
      return resultsStore.questions[questionName]

    case requestTypes.TAGS_RESULTS_PER_PERSON:
      const tagStatItemNames = Object.keys(requestData.params || {})
      for (const tagStatItemName of tagStatItemNames) {
        if (!resultsStore.tagsResultsPerPerson[tagStatItemName]) {
          await resultsStore.loadGroupResultsPerPerson({
            surveyName: surveyNameToLoad,
            groupName,
            statItemName: tagStatItemName,
            requestType: requestTypes.TAGS_RESULTS_PER_PERSON,
            aggregate,
            oldSurveys,
            selectedAttributeSegment
          })
        }
      }
      return resultsStore.tagsResultsPerPerson

    case requestTypes.CATEGORIES_RESULTS_PER_PERSON:
      const categoryStatItemName = Object.keys(requestData.params)[0]
      if (!resultsStore.categoriesResultsPerPerson[categoryStatItemName]) {
        await resultsStore.loadGroupResultsPerPerson({
          surveyName: surveyNameToLoad,
          groupName,
          statItemName: categoryStatItemName,
          requestType: requestTypes.CATEGORIES_RESULTS_PER_PERSON,
          aggregate,
          oldSurveys,
          selectedAttributeSegment
        })
      }
      return resultsStore.categoriesResultsPerPerson

    case requestTypes.RESPONSES:
      if (responsesStore.loading[requestTypes.RESPONSES] !== false) {
        await responsesStore.loadResponsesAction({
          surveyName: surveyNameToLoad,
          selectedAttributeSegment
        })
      }
      return responsesStore.responses

    case requestTypes.SURVEY_DETAILS:
      if (!surveysStore.surveyDetails) {
        await surveysStore.loadSurveyDetailsAction({
          surveyName: surveyNameToLoad,
          currentSurveysOnly: true
        })
      }
      return surveysStore.surveyDetails

    case requestTypes.SURVEYS:
      if (surveysStore.surveys.length === 0) {
        await surveysStore.loadSurveysAction({ currentSurveysOnly: true })
      }
      return surveysStore.surveys

    case requestTypes.FILTERED_SURVEY_DETAILS:
      if (
        !surveysStore.filteredSurveyDetails ||
        surveysStore.filteredSurveyDetails.name !== surveyNameToLoad
      ) {
        await surveysStore.loadFilteredSurveyDetailsAction({
          surveyName: surveyNameToLoad
        })
      }
      return surveysStore.filteredSurveyDetails

    case 'undefined':
      console.error('dataName is undefined', { dataName, widgetData })
      return undefined

    default:
      throw new Error(`Unknown data type requested: ${dataName}, ${widgetData}`)
  }
}
